import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { DateutilService } from 'app/shared/services/dateutil.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { CollectData, DataCollectionDialogResult } from 'app/shared/models/monitorConfiguration';
import { MonitorSeriesNames } from 'app/shared/components/location-card/location-card.constants';

const FLEX_BACK_COLLECT_FALLBACK_DAYS = 30;
const ONE_SECOND_MS = 1000;

@Component({
    templateUrl: './data-collection.component.html',
    styleUrls: ['./data-collection.component.scss'],
})
export class DataCollectionComponent implements OnInit, OnDestroy {
    /* Variable initilization section */
    private readonly today = new Date();
    public startDate: Date;
    public endDate: Date;
    public maxDate = new Date();
    public dayErrorMessage: string;
    public isDaySpanValid: boolean;
    public is12HourFormat = false;
    public dateFormat: string;
    public collectionHeader: string;
    public collectText: string;
    public closeButtonTranslationKey: 'COMMON.CLOSE' | 'COMMON.CANCEL' = 'COMMON.CLOSE';

    public dateErrMsg: string;
    public startGreaterThanTodayErr: string;
    public endGreaterThanTodayErr: string;
    // varibale to hold customer date format
    public customDateFormat: string;
    private subscriptions: Subscription[] = [];

    /*End section */

    /**
     * Component constructor
     * @param data
     * @param dialogRef
     * @param dateutilService
     * @param series
     */
    constructor(
        @Inject(MAT_DIALOG_DATA) public data: CollectData,
        public dialogRef: MatDialogRef<DataCollectionComponent, DataCollectionDialogResult>,
        private dateutilService: DateutilService,
        private translate: TranslateService,
    ) {}

    /**
     * Init Life cycle event.
     */
    public ngOnInit(): void {
        this.subscriptions.push(this.translate.get('COMMON.DATE_ERROR').subscribe((res: string) => {
            this.dateErrMsg = res;
        }));
        this.subscriptions.push(this.translate.get('COMMON.START_DATE_GREATER_TODAY').subscribe((res: string) => {
            this.startGreaterThanTodayErr = res;
        }));
        this.subscriptions.push(this.translate.get('COMMON.END_DATE_GREATER_TODAY').subscribe((res: string) => {
            this.endGreaterThanTodayErr = res;
        }));
        this.endDate = new Date(
            this.today.getFullYear(),
            this.today.getMonth(),
            this.today.getDate(),
            this.today.getHours() + 24,
            this.today.getMinutes(),
            this.today.getSeconds(),
        );
        this.collectionHeader = this.isFlexBackCollect ?
            this.translate.instant('HOME.COLLECTION_WIDGET.BACK_COLLECT_HEADER') :
            this.translate.instant('HOME.COLLECTION_WIDGET.DATA_COLLECTION_HEADER');

        this.collectText = this.isFlexBackCollect ?
            this.translate.instant('HOME.COLLECTION_WIDGET.BACK_COLLECT') :
            this.translate.instant('LOCATION_DASHBOARD.VIEW_DATA_LOCATION_DETAILS.COLLECT');
        this.closeButtonTranslationKey = this.isFlexBackCollect ? 'COMMON.CANCEL' : 'COMMON.CLOSE';

        // get last collected date
        this.isDaySpanValid = true;
        this.startDate = this.getStartDate();
        this.dateValidation();

        // set time format to UI
        this.dateFormat = this.dateutilService.getFormat();
        this.is12HourFormat = this.dateutilService.timeFormat.getValue() !== 'hh:mm:ss';
        this.customDateFormat = this.is12HourFormat
            ? `${this.dateFormat}, ${'hh:mm a'}`
            : `${this.dateFormat}, ${'HH:mm'}`;
    }

    /**
     * Close Dialog event
     */
    public close(): void {
        this.dialogRef.close({ success: false });
    }

    /**
     * On changing start date time event
     */
    public onChangeStartDateTime(event: MatDatepickerInputEvent<Date>): void {
        this.isDaySpanValid = true;
        this.startDate = event.target.value;
        this.dateValidation();
    }

    /**
     * On changing end date time event
     */
    public onChangeEndDateTime(event: MatDatepickerInputEvent<Date>): void {
        this.isDaySpanValid = true;
        this.endDate = event.target.value;
        this.dateValidation();
    }

    /**
     * Use to close dialog event and send data to back to the calling component.
     */
    public collect(): void {
        this.dialogRef.close({
            success: true,
            startDate: this.startDate,
            endDate: this.endDate,
        });
    }

    /**
     * Handle Start Date change event on calender popup
     * @param event
     */
    public onStartDateChanged(event: MatDatepickerInputEvent<Date>): void {
        this.startDate = event.value;
        this.dateValidation();
    }

    /**
     * Handle end Date change event on calender popup
     * @param event
     */
    public onEndDateChanged(event: MatDatepickerInputEvent<Date>): void {
        this.endDate = event.value;
        this.dateValidation();
    }

    public get isFlexBackCollect(): boolean {
        return this.data?.series?.toUpperCase() === MonitorSeriesNames.flex.toUpperCase();
    }

    private getStartDate(): Date {
        if (this.isFlexBackCollect) {
            return this.getFlexBackCollectStartDate();
        }

        const lastCollectDate = this.getValidDate(this.data?.lastCollectDate);
        if (lastCollectDate) {
            return lastCollectDate;
        }

        return new Date(
            this.today.getFullYear(),
            this.today.getMonth() - 1,
            this.today.getDate() - 1,
            0,
            0,
            0,
        );
    }

    private getFlexBackCollectStartDate(): Date {
        const telemetryEnd = this.getValidDate(this.data?.telemetryEnd);

        if (telemetryEnd) {
            return new Date(telemetryEnd.getTime() + ONE_SECOND_MS);
        }

        return new Date(
            this.today.getFullYear(),
            this.today.getMonth(),
            this.today.getDate() - FLEX_BACK_COLLECT_FALLBACK_DAYS,
            0,
            0,
            0,
        );
    }

    private getValidDate(date: Date | string | undefined): Date | null {
        if (!date) return null;

        const value = new Date(date);

        return Number.isNaN(value.getTime()) ? null : value;
    }

    /**
     * Use to Validate Dates(start/End)
     */
    private dateValidation(): void {
        this.dayErrorMessage = '';

        if (this.startDate && this.endDate) {
            this.isDaySpanValid = true;
            if (this.startDate > this.endDate) {
                this.isDaySpanValid = false;
                this.dayErrorMessage = this.dateErrMsg;
            }
        }
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(v => v.unsubscribe());
        this.subscriptions = [];
    }
}
